/**
 * ECDH Key Exchange for Secure Key Transfer
 */
import { decodeBase64Url, encodeBase64Url } from './codec.js';
/**
 * Generate an ECDH P-256 keypair for key transfer
 */
export async function generateECDHKeyPair() {
    const keyPair = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, [
        'deriveBits',
    ]);
    const pair = keyPair;
    const publicKeyJwk = await crypto.subtle.exportKey('jwk', pair.publicKey);
    return {
        publicKey: publicKeyJwk,
        privateKey: pair.privateKey,
    };
}
/**
 * Import an ECDH public key from JWK
 */
export async function importECDHPublicKey(jwk) {
    return crypto.subtle.importKey('jwk', jwk, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
}
/**
 * Create a key transfer envelope to securely send MMK to a new device
 */
export async function createKeyTransferEnvelope(mmk, recipientPubKey, identityId, requestId) {
    const ephemeral = await generateECDHKeyPair();
    const recipientKey = await importECDHPublicKey(recipientPubKey);
    const sharedBits = await crypto.subtle.deriveBits({ name: 'ECDH', public: recipientKey }, ephemeral.privateKey, 256);
    const salt = crypto.getRandomValues(new Uint8Array(32));
    const info = new TextEncoder().encode('sisumail:keytransfer:v1');
    const hkdfKey = await crypto.subtle.importKey('raw', sharedBits, { name: 'HKDF' }, false, [
        'deriveBits',
        'deriveKey',
    ]);
    const encryptionKey = await crypto.subtle.deriveKey({ name: 'HKDF', salt: salt, info: info, hash: 'SHA-256' }, hkdfKey, { name: 'AES-GCM', length: 256 }, false, ['encrypt']);
    const aad = `kt:v1:${identityId}:${requestId}`;
    const aadBuffer = new TextEncoder().encode(aad);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: aadBuffer }, encryptionKey, new Uint8Array(mmk));
    return {
        kt_v: 1,
        kex: 'ECDH-P256',
        kdf: 'HKDF-SHA256',
        enc: 'AES-256-GCM',
        salt: encodeBase64Url(salt),
        iv: encodeBase64Url(iv),
        pk: ephemeral.publicKey,
        ct: encodeBase64Url(ciphertext),
        aad,
    };
}
/**
 * Decrypt a key transfer envelope to recover MMK
 */
export async function decryptKeyTransferEnvelope(envelope, privateKey, identityId, requestId) {
    if (envelope.kt_v !== 1) {
        throw new Error(`Unsupported key transfer version: ${envelope.kt_v}`);
    }
    const expectedAad = `kt:v1:${identityId}:${requestId}`;
    if (envelope.aad !== expectedAad) {
        throw new Error('AAD mismatch - possible tampering');
    }
    const senderPubKey = await importECDHPublicKey(envelope.pk);
    const sharedBits = await crypto.subtle.deriveBits({ name: 'ECDH', public: senderPubKey }, privateKey, 256);
    const salt = decodeBase64Url(envelope.salt);
    const info = new TextEncoder().encode('sisumail:keytransfer:v1');
    const hkdfKey = await crypto.subtle.importKey('raw', sharedBits, { name: 'HKDF' }, false, [
        'deriveBits',
        'deriveKey',
    ]);
    const decryptionKey = await crypto.subtle.deriveKey({
        name: 'HKDF',
        salt: new Uint8Array(salt),
        info: info,
        hash: 'SHA-256',
    }, hkdfKey, { name: 'AES-GCM', length: 256 }, false, ['decrypt']);
    const aadBuffer = new TextEncoder().encode(envelope.aad);
    const iv = decodeBase64Url(envelope.iv);
    const ciphertext = decodeBase64Url(envelope.ct);
    const mmkBuffer = await crypto.subtle.decrypt({
        name: 'AES-GCM',
        iv: new Uint8Array(iv),
        additionalData: aadBuffer,
    }, decryptionKey, new Uint8Array(ciphertext));
    return new Uint8Array(mmkBuffer);
}
//# sourceMappingURL=ecdh.js.map